<template>
  <div class="top-five-sources">
    <div
      class="top-five-sources__heading d-flex align-items-center justify-content-between"
    >
      <h2 class="i-text-subheading-1">{{ componentTitle }}</h2>
      <i-toggle
        :toggle-value="showLeftToggle"
        :left-label="componentText.leftToggleLabel"
        :left-icon="componentText.leftToggleIcon"
        :right-label="componentText.rightToggleLabel"
        :right-icon="componentText.rightToggleIcon"
        @value-update="(v) => (showLeftToggle = v)"
      ></i-toggle>
    </div>

    <div class="top-five-sources__content i-mt-13">
      <div v-if="isLoaded">
        <top-five-doughnut-chart-canvas
          :type="type"
          :chart-title="componentText.chartTitle"
          :chart-data="activeDataset"
          :table-heading="tableHeading"
          :is-category="!showLeftToggle"
        />
      </div>
      <div v-if="!isLoaded">
        <i-spinner size="xl" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { iToggle, iSpinner } from '@inscribe/signal';
import TopFiveDoughnutChartCanvas from '@/V2/credit/charts/top-five-doughnut-chart/TopFiveDoughnutChartCanvas';
import componentText from '@/V2/credit/granular-insights/components/components.text';
import {
  getTransactionAmountProperty,
  normalizeText,
} from '@/V2/credit/granular-insights/components/components.utils';
import creditTransactionsCategories from '@/V2/credit/components/CreditTransactionsCategories.content';
import { LOAN_INSIGHTS_SLUG } from '@/V2/credit/granular-insights/constants';
import { $event } from '@/bus';
import { getAndSyncQueryParams } from '@/utils/queryParams';
import { getUseAllDocsQueryParameter } from '@/V2/credit/utils';

const NUM_OF_RESULTS = 4;

export default {
  components: {
    TopFiveDoughnutChartCanvas,
    iToggle,
    iSpinner,
  },
  props: {
    type: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      merchantDataLoading: false,
      categoryDataLoading: false,
      showLeftToggle: false,
      leftToggleDataset: [],
      rightToggleDataset: [],
      params: getAndSyncQueryParams({
        fullPath: this.$route.fullPath,
        router: this.$router,
        store: this.$store,
      }),
    };
  },
  computed: {
    ...mapGetters({
      merchantTransactionData: 'insightsTopTransactionsByMerchant',
      categoryTransactionData: 'insightsTopTransactionsByCategory',
    }),

    componentText() {
      return componentText[this.type].topFiveSources;
    },

    componentTitle() {
      return this.showLeftToggle
        ? this.componentText.leftToggleTitle
        : this.componentText.rightToggleTitle;
    },

    tableHeading() {
      return this.showLeftToggle
        ? this.componentText.leftToggleLabel
        : this.componentText.rightToggleLabel;
    },

    activeDataset() {
      return this.showLeftToggle
        ? this.leftToggleDataset
        : this.rightToggleDataset;
    },

    hasChartData() {
      return this.activeDataset.length > 0;
    },

    isLoanInsights() {
      return this.type === LOAN_INSIGHTS_SLUG;
    },

    isLoaded() {
      return !this.merchantDataLoading && !this.categoryDataLoading;
    },
    useAllDocsQueryParameter() {
      return getUseAllDocsQueryParameter(this.params);
    },
  },
  watch: {
    '$route.fullPath': {
      handler: function (newFullPath) {
        const oldUseAllDocsValue = this.params?.use_all_docs;
        this.params = getAndSyncQueryParams({
          fullPath: newFullPath,
          router: this.$router,
          store: this.$store,
        });
        if (oldUseAllDocsValue !== this.params?.use_all_docs) {
          this.updateAllData();
        }
      },
    },
  },
  created() {
    this.updateAllData();
    $event.$on('credit:refresh', async () => {
      this.updateAllData();
    });
  },
  methods: {
    ...mapActions([
      'getCustomerTransactionsByMerchant',
      'getCustomerTransactionsByCategory',
    ]),

    updateAllData() {
      this.fetchTopMerchants();
      this.fetchTopCategories();
    },

    dataFormatMapper(transactions, sourceProperty) {
      return transactions
        .filter((transaction) => {
          // Filter out transactions where sourceProperty is empty or undefined
          // or where the parent category is LOAN or the amount value is 0
          return (
            transaction[sourceProperty] !== undefined &&
            transaction[sourceProperty] !== '' &&
            transaction[sourceProperty] !== null &&
            // LOAN is a parent category that should be not be displayed on its own but grouped as part of the Other Loan Providers section
            transaction[sourceProperty] !== 'LOAN' &&
            // unknown lender/payer is a value that should be not be displayed on its own but grouped as part of the Other Loan Providers section
            transaction[sourceProperty].toLowerCase() !== 'unknown lender' &&
            transaction[sourceProperty].toLowerCase() !== 'unknown payer' &&
            transaction[getTransactionAmountProperty(this.type)] !== 0
          );
        })
        .map((transaction) => {
          const mappedTransaction = {
            source:
              sourceProperty === 'merchant'
                ? transaction[sourceProperty]
                : this.getCategoryLabel(transaction[sourceProperty]),
            amount: transaction[getTransactionAmountProperty(this.type)],
          };

          if (this.isLoanInsights) {
            mappedTransaction.disbursement = transaction.credit_amount;
          }
          return mappedTransaction;
        });
    },

    async fetchTopMerchants() {
      this.merchantDataLoading = true;

      const queryParams = {
        category: this.type,
        include_unreconciled_documents: this.useAllDocsQueryParameter,
      };

      await this.getCustomerTransactionsByMerchant({
        customerId: this.$route.params.customer_id,
        queryParams,
      });

      this.prepareMerchantDataset();
      this.merchantDataLoading = false;
    },

    prepareMerchantDataset() {
      if (this.merchantTransactionData.length === 0) {
        this.leftToggleDataset = this.merchantTransactionData;
        this.showLeftToggle = false;
      } else {
        const topMerchants = this.dataFormatMapper(
          this.merchantTransactionData,
          'merchant',
        );
        this.leftToggleDataset = [...topMerchants.slice(0, NUM_OF_RESULTS)];
      }
    },

    async fetchTopCategories() {
      this.categoryDataLoading = true;

      const queryParams = {
        category: this.type,
        include_unreconciled_documents: this.useAllDocsQueryParameter,
      };

      await this.getCustomerTransactionsByCategory({
        customerId: this.$route.params.customer_id,
        queryParams,
      });

      this.prepareCategoryDataset();
      this.categoryDataLoading = false;
    },

    prepareCategoryDataset() {
      if (this.categoryTransactionData.length === 0) {
        this.rightToggleDataset = this.categoryTransactionData;
      } else {
        const topCategories = this.dataFormatMapper(
          this.categoryTransactionData,
          'category',
        );
        this.rightToggleDataset = [...topCategories.slice(0, NUM_OF_RESULTS)];
      }
    },

    getCategoryLabel(category) {
      return (
        creditTransactionsCategories[category]?.label || normalizeText(category)
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.top-five-sources {
  &__chart-container {
    min-height: 305px;
  }
}
</style>
